import { useState, useRef, useCallback } from 'react'
import { useTonWallet, useTonConnectUI } from '@tonconnect/ui-react'
import { Copy, LogOut, Check, WalletMinimal } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { haptics } from '../../lib/haptics'

/** CRC16-CCITT (poly 0x1021, init 0) — used for TON friendly address checksum */
function crc16(data: Uint8Array): number {
  let crc = 0
  for (const byte of data) {
    let x = (crc >> 8) ^ byte
    x ^= x >> 4
    crc = ((crc << 8) ^ (x << 12) ^ (x << 5) ^ x) & 0xffff
  }
  return crc
}

/**
 * Convert TonConnect raw address ("workchain:hexaddr") to
 * UQ... non-bounceable friendly format — no @ton/core needed.
 */
function toUQ(raw: string): string {
  try {
    const colon = raw.indexOf(':')
    if (colon === -1) return raw
    const wc = parseInt(raw.slice(0, colon), 10)
    const hex = raw.slice(colon + 1)
    if (hex.length !== 64) return raw

    const addr = new Uint8Array(32)
    for (let i = 0; i < 32; i++) addr[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)

    const buf = new Uint8Array(34)
    buf[0] = 0x51
    buf[1] = wc < 0 ? wc + 256 : wc
    buf.set(addr, 2)

    const cs = crc16(buf)
    const full = new Uint8Array(36)
    full.set(buf)
    full[34] = (cs >> 8) & 0xff
    full[35] = cs & 0xff

    let bin = ''
    for (const b of full) bin += String.fromCharCode(b)
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_')
  } catch {
    return raw
  }
}

function formatAddress(uq: string): string {
  if (uq.length <= 10) return uq
  return `${uq.slice(0, 4)}…${uq.slice(-4)}`
}

interface DropdownPos {
  top: number
  right: number
}

export default function WalletChip() {
  const wallet = useTonWallet()
  const [tonConnectUI] = useTonConnectUI()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [pos, setPos] = useState<DropdownPos>({ top: 0, right: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)

  if (!wallet) return null

  const rawAddr = wallet.account.address
  const uqAddr = toUQ(rawAddr)
  const display = formatAddress(uqAddr)

  const handleOpen = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect()
    if (rect) {
      setPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      })
    }
    haptics.impact('light')
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => setOpen(false), [])

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(uqAddr)
      haptics.notification('success')
      setCopied(true)
      setTimeout(() => { setCopied(false); handleClose() }, 1200)
    } catch {
      handleClose()
    }
  }

  const disconnect = async () => {
    handleClose()
    await tonConnectUI.disconnect()
  }

  return (
    <>
      {/* Trigger */}
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full min-h-[32px] active:opacity-70 transition-opacity"
        style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
      >
        <WalletMinimal
          size={13}
          strokeWidth={2}
          className="shrink-0"
          style={{ color: 'var(--tg-theme-hint-color)' }}
        />
        <span
          className="text-[13px] font-medium"
          style={{ color: 'var(--tg-theme-hint-color)' }}
        >
          {display}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Invisible backdrop — catches outside taps */}
            <motion.div
              className="fixed inset-0 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              onClick={handleClose}
            />

            {/* Dropdown card */}
            <motion.div
              className="fixed z-50 overflow-hidden select-none"
              style={{
                top: pos.top,
                right: pos.right,
                minWidth: 180,
                maxWidth: 'calc(100vw - 32px)',
                backgroundColor: 'var(--tg-theme-bg-color)',
                borderRadius: 14,
                boxShadow: 'var(--shadow-dropdown)',
                WebkitUserSelect: 'none',
                userSelect: 'none',
              }}
              initial={{ opacity: 0, scale: 0.85, transformOrigin: 'top right' }}
              animate={{ opacity: 1, scale: 1, transformOrigin: 'top right' }}
              exit={{ opacity: 0, scale: 0.85, transformOrigin: 'top right' }}
              transition={{ duration: 0.15, ease: [0.32, 0, 0.67, 0] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Copy address row */}
              <button
                onClick={copyAddress}
                className="w-full flex items-center gap-3 px-4 active:opacity-70 transition-opacity overflow-hidden"
                style={{ height: 48 }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {copied ? (
                    <motion.span
                      key="check"
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.7 }}
                      transition={{ duration: 0.15 }}
                      className="shrink-0"
                    >
                      <Check size={16} strokeWidth={2.5} style={{ color: 'var(--tg-theme-button-color)' }} />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="copy"
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.7 }}
                      transition={{ duration: 0.15 }}
                      className="shrink-0"
                    >
                      <Copy size={16} strokeWidth={2} style={{ color: 'var(--tg-theme-hint-color)' }} />
                    </motion.span>
                  )}
                </AnimatePresence>
                <AnimatePresence mode="wait" initial={false}>
                  {copied ? (
                    <motion.span
                      key="copied-text"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.15 }}
                      className="text-[14px] font-medium"
                      style={{ color: 'var(--tg-theme-button-color)' }}
                    >
                      Copied!
                    </motion.span>
                  ) : (
                    <motion.span
                      key="copy-text"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.15 }}
                      className="text-[14px] font-medium"
                      style={{ color: 'var(--tg-theme-text-color)' }}
                    >
                      Copy address
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>

              {/* Divider */}
              <div
                style={{
                  height: 1,
                  marginLeft: 16,
                  backgroundColor: 'var(--tg-theme-hint-color)',
                  opacity: 0.15,
                }}
              />

              {/* Disconnect row */}
              <button
                onClick={disconnect}
                className="w-full flex items-center gap-3 px-4 active:opacity-70 transition-opacity"
                style={{ height: 48 }}
              >
                <LogOut size={16} strokeWidth={2} style={{ color: 'var(--tg-theme-destructive-text-color)' }} />
                <span
                  className="text-[14px] font-medium"
                  style={{ color: 'var(--tg-theme-destructive-text-color)' }}
                >
                  Disconnect
                </span>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
